import type { ReactElement } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { Icon, type IconName } from '@/components/atoms/icon';
import { cn } from '@/lib/cn';

type RailItem = {
  readonly key: string;
  readonly to: '/' | '/repertoire' | '/practice' | '/history' | '/settings';
  readonly label: string;
  readonly icon: IconName;
  readonly matchPrefix?: readonly string[];
};

const ITEMS: readonly RailItem[] = [
  { key: 'today', to: '/', label: 'Today', icon: 'home' },
  { key: 'repertoire', to: '/repertoire', label: 'Repertoire', icon: 'book', matchPrefix: ['/repertoire'] },
  { key: 'practice', to: '/practice', label: 'Practice', icon: 'play', matchPrefix: ['/practice'] },
  { key: 'history', to: '/history', label: 'History', icon: 'chart', matchPrefix: ['/history', '/insights'] },
  { key: 'settings', to: '/settings', label: 'Settings', icon: 'cog', matchPrefix: ['/settings', '/profile'] },
] as const;

function isActive(pathname: string, item: RailItem): boolean {
  if (item.to === '/') return pathname === '/';
  const prefixes = item.matchPrefix ?? [item.to];
  return prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

export function TabletRail(): ReactElement {
  const location = useLocation();

  return (
    <aside
      data-testid="tablet-rail"
      aria-label="Primary"
      className="w-[84px] h-full bg-[var(--bg-surface)] border-r border-[var(--rule)] flex flex-col items-center py-4 shrink-0"
    >
      <div
        className="w-10 h-10 rounded-[var(--r-sm)] bg-[var(--ink)] flex items-center justify-center mb-5"
        aria-hidden
      >
        <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
          <path
            d="M10 9v14M10 9c2-1.5 6-1.5 8 0v14"
            stroke="var(--bg-page)"
            strokeWidth="1.75"
            strokeLinecap="round"
          />
          <circle cx="10" cy="22" r="2" fill="var(--bg-page)" />
          <circle cx="18" cy="22" r="2" fill="var(--warm)" />
        </svg>
      </div>
      <nav className="flex flex-col gap-1 flex-1">
        {ITEMS.map((item) => {
          const active = isActive(location.pathname, item);
          return (
            <Link
              key={item.key}
              to={item.to}
              className={cn(
                'w-16 h-16 rounded-[var(--r-md)] flex flex-col items-center justify-center gap-1 transition-colors',
                active
                  ? 'bg-[var(--bg-soft)] text-[var(--ink)]'
                  : 'text-[var(--ink-3)] hover:text-[var(--ink)] hover:bg-[var(--bg-soft)]',
              )}
            >
              <Icon name={item.icon} size={18} />
              <span className="text-[9px] font-semibold tracking-wider uppercase">{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}
